import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import OrderSummaryPage from '../../components/payments/OrderSummaryPage';
import AsyncFiltersDisplay from '../../components/AsyncFiltersDisplay';
import Button from '../../components/inputs/Button';
import ThemeLink from '../../components/ThemeLink';
import { PURCHASED_AUDIENCE_QUERY } from '../../queries';
import ErrorText from '../../components/ErrorText';
import { getProduct } from '../../helpers/products';
import AddOnItem from './AudienceUpsell/AddOnItem';
import { useAuth } from '../../hooks/useAuth';

function OrderLabel({ order }) {
  return (
    <p>
      Audience Name: <span className="font-bold">{order.audienceName}</span>
    </p>
  );
}

function OrderDetails({ order }) {
  const { user } = useAuth();
  const { plan } = user;
  const { id: audienceProductId } = getProduct('audience', plan);

  const addOnProducts = order.invoice?.lineItems?.filter(
    (p) => p.id !== audienceProductId
  );

  return (
    <>
      <div className="mb-10">
        <h4 className="font-bold text-xl">Audience Details</h4>
        <h4 className="mb-2 font-bold text-xl">Included</h4>
        <div className="">
          <AsyncFiltersDisplay audienceName={order.audienceName} />
        </div>
      </div>
      {addOnProducts?.length > 0 && (
        <div className="mb-10">
          <h4 className="mb-2 font-bold text-xl">Adds Ons</h4>
          {addOnProducts.map((addOnProduct, idx) => (
            <div key={idx}>
              <AddOnItem
                product={addOnProduct}
                showDelete={false}
                showDiscount={false}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function DownloadAudienceButton({ audienceName, onDownloadStarted }) {
  const { loading, error, data } = useQuery(PURCHASED_AUDIENCE_QUERY, {
    variables: { audienceName },
    fetchPolicy: 'no-cache',
  });

  if (loading)
    return (
      <Button type="button" disabled className="min-w-max animate-pulse">
        Retrieving your download...
      </Button>
    );

  if (error)
    return (
      <div className="relative">
        <Button type="button" disabled className="min-w-max">
          Download Audience
        </Button>
        <div className="absolute mt-1">
          <ErrorText
            message={
              error.message.toLowerCase().includes('download pii')
                ? error.message
                : 'Failed to retrieve your download.'
            }
            className="w-64 text-sm"
          />
        </div>
      </div>
    );

  return (
    <>
      {data?.purchasedAudience && (
        <a
          href={data?.purchasedAudience?.downloadLink}
          download
          onClick={onDownloadStarted}
        >
          <Button type="button" primary className="min-w-max">
            Download Audience
          </Button>
        </a>
      )}
    </>
  );
}

export default function AudienceOrderSummary() {
  // Set by useOrder's post-completion navigation when this order also
  // chained a profile report ("Complete and Profile").
  const location = useLocation();
  const profileOrderId = location.state?.profileOrderId;

  return (
    <OrderSummaryPage
      orderType="audience"
      OrderLabel={OrderLabel}
      OrderDetails={OrderDetails}
      DownloadButton={DownloadAudienceButton}
      banner={
        profileOrderId ? (
          <div className="mb-8 p-4 bg-gray-100 rounded-half border">
            <p className="text-lg">
              Your Profile Report is being generated — it will appear in{' '}
              <ThemeLink to="../../../ask-addy">Ask Addy</ThemeLink> in a few
              minutes.
            </p>
          </div>
        ) : null
      }
    />
  );
}
